'use client';

import clsx from 'clsx';
import { SportMatchModel } from '@nf/types/odds';
import { StreamingBar } from '../streaming-item';
import { StreamingControl } from '../streaming-item/streaming-control';
import { MatchInfoPanel } from './matchpanel-info';
import { useStreamingStore } from '@/store/streaming';
import { useAtomValue } from 'jotai';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { oddsPageAtom } from '@/atoms/odds-page';
import { VideoDeviceType, streamingService } from '@/services/streaming.service';
import pluginService from '@/services/discover-plugin.service';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { videoStyle } from './matchpanel.style';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAppearanceStore } from '@/store/appearance';
import { Chatroom } from '@/features/chatroom';
import { useAccountInfo } from '@/hooks/query/account';
import { ViewEnum } from '@/types';

export interface MatchPanelProps {
	match: SportMatchModel;
	isAsideLayout: boolean;
	device: VideoDeviceType;
}

export const MatchPanel = ({ match, isAsideLayout, device }: MatchPanelProps) => {
	const t = useTranslations('General');
	const { user } = useAccountInfo()
	const view = useAppearanceStore(state => state.view);
	const oddsData = useAtomValue(oddsPageAtom);
	const { SiteConfigsData } = useGetSiteConfigs();
	const { hasStreamer } = streamingService.getVideoOptions(match, false, SiteConfigsData, user);
	const homeTeamName = getHomeTeamName(match.TeamId1, match.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(match.TeamId2, oddsData?.teamNameMap);
	const channelList = streamingService.getChannelList([t('lbl_Streamer'), t.raw('lbl_Source')], match, SiteConfigsData, user);
	const [defaultStreamingSrc, setDefaultStreamingSrc] = useState<string>('');

	const [
		streamingType,
		isPin,
		setIsPin,
		isStreamingFloat,
		setIsStreamingFloat,
		videoSpecial,
		streamingSrc
	] = useStreamingStore(
		state => [
			state.streamingType,
			state.isPin,
			state.setIsPin,
			state.isStreamingFloat,
			state.setIsStreamingFloat,
			state.videoSpecial,
			state.streamingSrc
		]
	);

	const willUseStreaminSrc = (streamingSrc ? streamingSrc : defaultStreamingSrc);
	const realSrc = willUseStreaminSrc.split('-')[0];
	const params = {
		streamingSrc: willUseStreaminSrc,
		realSrc: realSrc as string,
		streamerExclusiveId: match && !!realSrc ? streamingService.getVideoInfo(match, SiteConfigsData, user, willUseStreaminSrc)?.siteId || 0 : 0,
		matchId: match ? match?.MatchId : 0,
		isPin: isPin,
		parentId: match ? match?.Parentid || 0 : 0,
	};

	const topBarHeight = document.querySelector<HTMLElement>('.single-topbar')?.clientHeight;
	const scrollContainer = window;
	let origintargetBottomPosition = 0;
	const streamerSrcList = pluginService.getSupportStreamerSourceList();
	const isStreamingPlugin = SiteConfigsData?.EnableStreamingPlugin && pluginService.checkIsSupportSource(channelList, match);

	useEffect(() => {
		setIsPin(false);
		setIsStreamingFloat(false);
	}, [streamingType]);

	useEffect(() => {
		const defaultStreamingSrc = channelList.find(channel => {
			const src = channel.value.split('-')[0];
			return streamerSrcList.includes(src);
		})?.value ?? '';

		setDefaultStreamingSrc(defaultStreamingSrc);
	}, [channelList]);

	useEffect(() => {
		const handleScroll = () => {
			if (streamingType === 'SlmtGV' || streamingType === 'Streaming' || streamingType === 'Streamer' || streamingType === 'GV' || (streamingType === 'StaticInfo' && match.GameID === 50)) {
				const targetVideoElement = document.querySelector<HTMLElement>('.video');
				const videoViewElement = document.querySelector<HTMLElement>('.video-view-ref');
				if (scrollContainer && targetVideoElement) {
					const targetPosition = targetVideoElement.offsetTop;
					const scrollPosition = (scrollContainer as Window).scrollY;
					if (scrollPosition < origintargetBottomPosition && videoViewElement) {
						setIsStreamingFloat(false);
						return;
					}
					const targetBottomPosition = targetPosition + targetVideoElement.offsetHeight;
					if (window.innerWidth > 768) {
						setIsStreamingFloat(false);
					} else if (scrollPosition >= targetBottomPosition && videoViewElement && isStreamingFloat === false) {
						origintargetBottomPosition = targetBottomPosition
						setIsStreamingFloat(true);
					}
				}
			}
		};

		if (!isAsideLayout) {
			if (scrollContainer) {
				scrollContainer.addEventListener('scroll', handleScroll);
			}
			return () => {
				if (scrollContainer) {
					scrollContainer.removeEventListener('scroll', handleScroll);
				}
			};
		}
	}, [streamingType, isAsideLayout]);

	const streamingPluginType = (user?.ActStatus === 2 && isStreamingPlugin) ? streamingService.checkPluginType(streamingType, SiteConfigsData?.EnableChatroom || false, hasStreamer) : 0;

	return (
		<div
			className={clsx(
				'video',
				{ 'video--special': videoSpecial },
				videoStyle.video,
				isStreamingFloat ? videoStyle.videoFloatBg : videoStyle.videoBg,
			)}
			data-pin={isPin}
			data-float={!isPin && isStreamingFloat}
			data-streaming={streamingType !== ''}
			data-lsport={streamingType === 'GV'}
			style={{ '--single-topbar-height': `${topBarHeight}px` } as React.CSSProperties}
		>
			<StreamingBar
				match={match}
				isStreamingBar={streamingType ? true : false}
				home={homeTeamName}
				away={awayTeamName}
				isAsideLayout={isAsideLayout}
				isStreamingPlugin={isStreamingPlugin}
			/>
			{
				(user?.ActStatus === 2 && isStreamingPlugin && streamingPluginType > 0 && streamingType !== 'StaticInfo') ?
					<>
						{(!streamingType && streamingPluginType === 3) &&
							<MatchInfoPanel
								match={match}
								home={homeTeamName}
								away={awayTeamName}
								isStreamingPlugin={isStreamingPlugin}
							/>}
						<StreamingControl
							match={match}
							device={device}
							isStreamingPlugin={true}
							streamingPluginType={streamingPluginType}
						/>
					</> :
					streamingType ?
						<StreamingControl
							match={match}
							device={device}
						/> :
						<MatchInfoPanel
							match={match}
							home={homeTeamName}
							away={awayTeamName}
							isStreamingPlugin={isStreamingPlugin}
						/>
			}
			{
				SiteConfigsData?.EnableChatroom && match?.IsLive && willUseStreaminSrc && (view === ViewEnum.mars) && streamerSrcList.includes(realSrc) && !isStreamingPlugin ?
					<Chatroom params={params} /> :
					null
			}
		</div>
	);
};